import { ReactNode, useMemo, useState } from "react"
import { Project } from "@/schema/project"
import { InlineSelect } from "@ui/forms/inline"
import { type LucideIcon } from "lucide-react"

import { useInfiniteProjectsWithFilters } from "@/hooks/api/projects/queries"

import { InputOption } from "../props"

export interface InlineProjectSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedProjectId: string | null
  selectedProject?: Project | null
  householdId?: string | null
  onSave?: (projectId: string | null, milestoneId: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  className?: string
  bookId?: string
}

export function InlineProjectSelect({
  label,
  selectedProjectId,
  selectedProject,
  onSave,
  isDisabled,
  fixedLabelWidth,
  hideLabel,
  LabelIcon,
  size,
  className,
  bookId,
}: InlineProjectSelectProps) {
  const [search, setSearch] = useState("")

  const { data, isLoading } = useInfiniteProjectsWithFilters({
    searchTerm: search,
    filters: [],
    enabled: true,
    bookIdOverride: bookId,
  })

  const activeProjects = useMemo(() => {
    return data?.projects?.filter((p) => p.status === "in_progress" || p.status === "overdue") ?? []
  }, [data?.projects])

  const options: InputOption[] = useMemo(() => {
    const projectOptions: InputOption[] = activeProjects.map((project) => ({
      value: project.id,
      label: project.name,
      description: project.project_template?.title,
    }))

    // If there's a selected project that's not in the active projects list, add it
    if (selectedProjectId && selectedProject && !activeProjects.some((p) => p.id === selectedProjectId)) {
      projectOptions.unshift({
        value: selectedProject.id,
        label: selectedProject.name,
      })
    }

    return projectOptions
  }, [activeProjects, selectedProjectId, selectedProject])

  const renderProjectOption = (option: InputOption) => {
    return (
      <div className="flex flex-col">
        <span className="font-medium">{option.label}</span>
        {option.description && <span className="text-muted-foreground text-xs">{option.description}</span>}
      </div>
    )
  }

  const renderProjectValue = (option: InputOption | null) => {
    if (!option?.value) {
      return <span className="text-input-placeholder">-</span>
    }

    const project = selectedProject || activeProjects.find((p) => p.id === option.value)

    if (project) {
      return (
        <div className="flex items-center gap-1.5">
          <span>{project.name}</span>
        </div>
      )
    }

    return <span>{option.label}</span>
  }

  const handleSave = async (projectId: string | null) => {
    if (!projectId) {
      await onSave?.(null, null)
      return
    }

    const project = activeProjects.find((p) => p.id === projectId)
    const milestoneId = project?.project_milestone_id ?? null
    await onSave?.(projectId, milestoneId)
  }

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={selectedProjectId}
      placeholder="-"
      options={options}
      disabled={isLoading || isDisabled}
      onSave={handleSave}
      onSearch={setSearch}
      searchPlaceholder="Search projects..."
      emptyMessage="No active projects"
      hideLoader
      fixedLabelWidth={fixedLabelWidth}
      renderValue={renderProjectValue}
      renderOption={renderProjectOption}
      hideLabel={hideLabel}
      size={size}
      className={className}
      clearLabel="Remove from project"
    />
  )
}
